import { useState, useEffect } from 'react';
import './HistoryPanel.css';

const ACTION_LABELS = {
    'create': 'Creat',
    'edit': 'Editat',
    'delete': 'Eliminat'
};

const FIELD_LABELS = {
    'nom': 'Nom',
    'mote': 'Sobrenom',
    'primer-cognom': '1r cognom',
    'segon-cognom': '2n cognom',
    'altura': 'Altura',
    'altura_mans': 'Altura mans',
    'mail': 'Email',
    'telefon': 'Telèfon',
    'es_tecnica': 'Tècnica',
    'es_junta': 'Junta',
    'canalla': 'Canalla',
    'music': 'Músic',
    'canpiulada': 'Canpiulada',
    'data_piulada': 'Data piulada',
    'data_inscripcio': 'Data inscripció',
    'data_naixement': 'Data naixement',
    'hidden': 'Amagat',
    'novell': 'Novell',
    'extern': 'Extern',
    'lesionat': 'Lesionat',
    'casteller': 'Casteller'
};

function HistoryPanel({ socket, history: externalHistory, onRefresh }) {
    const [isOpen, setIsOpen] = useState(false);

    const history = externalHistory || [];

    const togglePanel = () => {
        if (!isOpen && onRefresh) {
            onRefresh();
        }
        setIsOpen(!isOpen);
    };

    const loadHistory = () => {
        if (onRefresh) {
            onRefresh();
        }
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Ara mateix';
        if (diffMins < 60) return `Fa ${diffMins}m`;
        if (diffHours < 24) return `Fa ${diffHours}h`;
        if (diffDays < 7) return `Fa ${diffDays}d`;
        
        return date.toLocaleDateString('ca-ES', { 
            day: 'numeric', 
            month: 'short'
        });
    };

    const getCastellerName = (entry) => {
        // Use target_name from the history entry if available
        if (entry.target_name) {
            return entry.target_name;
        }
        
        if (entry.snapshot) {
            try {
                const snapshot = typeof entry.snapshot === 'string' 
                    ? JSON.parse(entry.snapshot) 
                    : entry.snapshot;
                
                // New format: snapshot.previousState contains the casteller data
                if (snapshot.previousState) {
                    return snapshot.previousState.mote || snapshot.previousState.nom || `Casteller #${entry.casteller_id}`;
                }
                
                // Old format: snapshot is the casteller data directly
                return snapshot.mote || snapshot.nom || `Casteller #${entry.casteller_id}`;
            } catch {
                return `Casteller #${entry.casteller_id}`;
            }
        }
        return `Casteller #${entry.casteller_id}`;
    };

    const getChangedFields = (entry) => {
        if (entry.action === 'create') return 'Nou';
        if (entry.action === 'delete') return 'Tot';
        
        if (!entry.snapshot) return '-';
        
        try {
            const snapshot = typeof entry.snapshot === 'string' 
                ? JSON.parse(entry.snapshot) 
                : entry.snapshot;
            
            // New format: snapshot has changedFields array
            if (snapshot.changedFields && Array.isArray(snapshot.changedFields)) {
                const fields = snapshot.changedFields
                    .map(key => FIELD_LABELS[key] || key)
                    .slice(0, 3);
                
                if (fields.length === 0) return '-';
                const suffix = snapshot.changedFields.length > 3 ? '...' : '';
                return fields.join(', ') + suffix;
            }
            
            // Old format fallback: snapshot is the full previous state
            const fields = Object.keys(snapshot)
                .filter(key => !['id', 'mote', 'nom', 'previousState', 'changedFields'].includes(key))
                .map(key => FIELD_LABELS[key] || key)
                .slice(0, 3);
            
            if (fields.length === 0) return '-';
            return fields.join(', ');
        } catch {
            return '-';
        }
    };

    return (
        <div className="history-panel-container">
            {/* Floating toggle button */}
            <div className="history-panel-floating-buttons">
                <button
                    className={`history-panel-toggle-btn ${isOpen ? 'history-panel-toggle-btn--open' : ''}`}
                    onClick={togglePanel}
                    title="Historial de canvis"
                >
                    <i className="fas fa-history"></i>
                </button>
            </div>

            {/* Slide-out panel */}
            <div className={`history-panel-slide ${isOpen ? 'history-panel-slide--open' : ''}`}>
                <div className="history-panel-header">
                    <h3 className="history-panel-title">
                        <i className="fas fa-history"></i>
                        Historial de canvis
                    </h3>
                    <button 
                        className="history-panel-close-btn"
                        onClick={() => setIsOpen(false)}
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="history-panel-content">
                    {history.length === 0 ? (
                        <div className="history-panel-empty">
                            <i className="fas fa-inbox"></i>
                            <p className="history-panel-empty-text">No hi ha canvis recents</p>
                        </div>
                    ) : (
                        <table className="history-panel-table">
                            <thead className="history-panel-thead">
                                <tr className="history-panel-header-row">
                                    <th className="history-panel-th">Acció</th>
                                    <th className="history-panel-th">Casteller</th>
                                    <th className="history-panel-th">Camps</th>
                                    <th className="history-panel-th">Autor</th>
                                    <th className="history-panel-th">Temps</th>
                                </tr>
                            </thead>
                            <tbody className="history-panel-tbody">
                                {history.map((entry, index) => (
                                    <tr key={entry.id} className={`history-panel-row ${index === 0 ? 'history-panel-row--latest' : ''}`}>
                                        <td className="history-panel-td">
                                            <span className={`history-panel-action-badge history-panel-action-badge--${entry.action}`}>
                                                {ACTION_LABELS[entry.action]}
                                            </span>
                                        </td>
                                        <td className="history-panel-td history-panel-casteller-name">
                                            {getCastellerName(entry)}
                                        </td>
                                        <td className="history-panel-td history-panel-fields">
                                            {getChangedFields(entry)}
                                        </td>
                                        <td className="history-panel-td history-panel-actor-name">
                                            {entry.actor_name || 'Desconegut'}
                                        </td>
                                        <td className="history-panel-td history-panel-timestamp">
                                            {formatTimestamp(entry.timestamp)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="history-panel-footer">
                    <button 
                        className="history-panel-refresh-btn"
                        onClick={loadHistory}
                    >
                        <i className="fas fa-sync-alt"></i>
                        Actualitzar
                    </button>
                    <span className="history-panel-shortcut">
                        <kbd className="history-panel-kbd">Ctrl</kbd>+<kbd className="history-panel-kbd">Z</kbd> per desfer
                    </span>
                </div>
            </div>

            {/* Backdrop */}
            {isOpen ? (
                <div 
                    className="history-panel-backdrop"
                    onClick={() => setIsOpen(false)}
                />
            ) : null}
        </div>
    );
}

export default HistoryPanel;
